import { OrgUnitsApi, AccessRolesApi } from '../../api/orgAccess';

const SET_UI_FLAG = 'SET_ORG_ACCESS_UI_FLAG';
const SET_UNITS = 'SET_ORG_UNITS';
const SET_UNIT = 'SET_ORG_UNIT';
const REMOVE_UNIT = 'REMOVE_ORG_UNIT';
const SET_ROLES = 'SET_ACCESS_ROLES';
const SET_ROLE = 'SET_ACCESS_ROLE';
const REMOVE_ROLE = 'REMOVE_ACCESS_ROLE';
const SET_CATALOGUE = 'SET_ORG_ACCESS_CATALOGUE';
const SET_MODE = 'SET_ORG_ACCESS_MODE';

export const state = {
  units: [],
  roles: [],
  catalogue: {},
  visibilityScopes: [],
  settingKeys: [],
  defaultSettings: {},
  mode: 'off',
  modes: ['off', 'hybrid', 'strict'],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUnits: _state => _state.units,
  getRoles: _state => _state.roles,
  getCatalogue: _state => _state.catalogue,
  getVisibilityScopes: _state => _state.visibilityScopes,
  getSettingKeys: _state => _state.settingKeys,
  getDefaultSettings: _state => _state.defaultSettings,
  getMode: _state => _state.mode,
  getModes: _state => _state.modes,
  getUIFlags: _state => _state.uiFlags,
  // Units arranged as a real tree so the chart can render nested children.
  getUnitTree: _state => {
    const byId = new Map(
      _state.units.map(unit => [unit.id, { ...unit, children: [] }])
    );
    const roots = [];
    byId.forEach(unit => {
      if (unit.parent_id && byId.has(unit.parent_id)) {
        byId.get(unit.parent_id).children.push(unit);
      } else {
        roots.push(unit);
      }
    });
    return roots;
  },
};

export const actions = {
  async fetchUnits({ commit }) {
    commit(SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await OrgUnitsApi.get();
      commit(SET_UNITS, data);
    } finally {
      commit(SET_UI_FLAG, { isFetching: false });
    }
  },

  async createUnit({ commit }, payload) {
    commit(SET_UI_FLAG, { isCreating: true });
    try {
      const { data } = await OrgUnitsApi.create({ org_unit: payload });
      commit(SET_UNIT, data);
      return data;
    } finally {
      commit(SET_UI_FLAG, { isCreating: false });
    }
  },

  async updateUnit({ commit }, { id, ...payload }) {
    commit(SET_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await OrgUnitsApi.update(id, { org_unit: payload });
      commit(SET_UNIT, data);
      return data;
    } finally {
      commit(SET_UI_FLAG, { isUpdating: false });
    }
  },

  async deleteUnit({ commit }, id) {
    commit(SET_UI_FLAG, { isDeleting: true });
    try {
      await OrgUnitsApi.delete(id);
      commit(REMOVE_UNIT, id);
    } finally {
      commit(SET_UI_FLAG, { isDeleting: false });
    }
  },

  async assignMembers({ commit }, { id, members }) {
    const { data } = await OrgUnitsApi.assignMembers({ id, members });
    commit(SET_UNIT, data);
    return data;
  },

  async attachInboxes({ commit }, { id, inboxIds }) {
    const { data } = await OrgUnitsApi.attachInboxes({ id, inboxIds });
    commit(SET_UNIT, data);
    return data;
  },

  async fetchRoles({ commit }) {
    commit(SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await AccessRolesApi.get();
      commit(SET_ROLES, data);
    } finally {
      commit(SET_UI_FLAG, { isFetching: false });
    }
  },

  async createRole({ commit }, payload) {
    commit(SET_UI_FLAG, { isCreating: true });
    try {
      const { data } = await AccessRolesApi.create({ access_role: payload });
      commit(SET_ROLE, data);
      return data;
    } finally {
      commit(SET_UI_FLAG, { isCreating: false });
    }
  },

  async updateRole({ commit }, { id, ...payload }) {
    commit(SET_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await AccessRolesApi.update(id, {
        access_role: payload,
      });
      commit(SET_ROLE, data);
      return data;
    } finally {
      commit(SET_UI_FLAG, { isUpdating: false });
    }
  },

  async deleteRole({ commit }, id) {
    commit(SET_UI_FLAG, { isDeleting: true });
    try {
      await AccessRolesApi.delete(id);
      commit(REMOVE_ROLE, id);
    } finally {
      commit(SET_UI_FLAG, { isDeleting: false });
    }
  },

  async fetchCatalogue({ commit }) {
    const { data } = await AccessRolesApi.catalogue();
    commit(SET_CATALOGUE, data);
  },

  async updateMode({ commit }, mode) {
    const { data } = await AccessRolesApi.updateMode(mode);
    commit(SET_MODE, data.mode);
    return data.mode;
  },
};

export const mutations = {
  [SET_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [SET_UNITS](_state, units) {
    _state.units = units;
  },
  [SET_UNIT](_state, unit) {
    const index = _state.units.findIndex(record => record.id === unit.id);
    if (index > -1) _state.units.splice(index, 1, unit);
    else _state.units.push(unit);
  },
  [REMOVE_UNIT](_state, id) {
    _state.units = _state.units.filter(record => record.id !== id);
  },
  [SET_ROLES](_state, roles) {
    _state.roles = roles;
  },
  [SET_ROLE](_state, role) {
    const index = _state.roles.findIndex(record => record.id === role.id);
    if (index > -1) _state.roles.splice(index, 1, role);
    else _state.roles.push(role);
  },
  [REMOVE_ROLE](_state, id) {
    _state.roles = _state.roles.filter(record => record.id !== id);
  },
  [SET_CATALOGUE](_state, payload) {
    _state.catalogue = payload.catalogue || {};
    _state.visibilityScopes = payload.visibility_scopes || [];
    _state.settingKeys = payload.settings || [];
    _state.defaultSettings = payload.default_settings || {};
    _state.mode = payload.mode || 'off';
    _state.modes = payload.modes || ['off', 'hybrid', 'strict'];
  },
  [SET_MODE](_state, mode) {
    _state.mode = mode;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
